<template>
  <div class="hold-transition">
    <div class="conecta-page" :class="{ 'conecta-page--loaded': loaded }">
      <!-- Header personalizable o por defecto -->
      <header v-if="$slots.header" class="conecta-page__header">
        <slot name="header" />
      </header>
      <header v-else class="conecta-page__header">
        <div class="conecta-page__header-bg" aria-hidden="true"></div>
        <div class="conecta-page__header-content">
          <button
            v-if="showBack"
            type="button"
            class="conecta-page__back"
            @click="goBack"
          >
            <i class="fas fa-arrow-left mr-2" aria-hidden="true"></i>
            Volver al inicio
          </button>
          <div class="conecta-page__brand">
            <div class="conecta-page__logo">
              <img
                src="/img/Logo-STS.png"
                alt="Conecta Sabana"
                class="conecta-page__logo-img"
              />
            </div>
            <div class="conecta-page__brand-text">
              <span class="conecta-page__eyebrow">{{ eyebrow }}</span>
              <h1 class="conecta-page__title">{{ title }}</h1>
              <p v-if="subtitle" class="conecta-page__subtitle">{{ subtitle }}</p>
            </div>
          </div>
          <p v-if="description" class="conecta-page__intro">{{ description }}</p>
        </div>
      </header>

      <main class="conecta-page__main" :class="{ 'conecta-page__main--narrow': narrow }">
        <slot />
      </main>

      <footer class="conecta-page__footer">
        <p class="conecta-page__footer-text">
          Sistema de pago electrónico multi-tarifa · Transporte de pasajeros
        </p>
        <p class="conecta-page__footer-copy">
          © {{ currentYear }} Conecta Sabana. Todos los derechos reservados.
        </p>
      </footer>
    </div>
  </div>
</template>

<script>
export default {
  name: "UsuariosExtLayout",

  props: {
    title: {
      type: String,
      default: "Conecta Sabana",
    },
    subtitle: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    eyebrow: {
      type: String,
      default: "Portal de autoservicio",
    },
    showBack: {
      type: Boolean,
      default: false,
    },
    backRoute: {
      type: String,
      default: "/conecta-sabana",
    },
    narrow: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loaded: false,
    };
  },

  computed: {
    currentYear() {
      return new Date().getFullYear();
    },
  },

  mounted() {
    requestAnimationFrame(() => {
      this.loaded = true;
    });
  },

  methods: {
    goBack() {
      this.$router.push(this.backRoute);
    },
  },
};
</script>

<style scoped lang="scss">
$green-light: #8fc74b;
$teal: #61a198;
$mint: #cde7d6;
$bg-soft: #e3f1e9;

.conecta-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: "Poppins", "Source Sans Pro", sans-serif;
  background:
    radial-gradient(ellipse at 20% 0%, rgba($green-light, 0.18) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 100%, rgba($teal, 0.12) 0%, transparent 50%),
    linear-gradient(180deg, $bg-soft 0%, #ffffff 40%, $mint 100%);
  opacity: 0;
  transition: opacity 0.6s ease;

  &--loaded {
    opacity: 1;
  }
}
</style>

<!-- Estilos globales compartidos del portal usuariosExt -->
<style lang="scss">
$green-primary: #05a85a;
$green-dark: #0d3d2b;
$green-light: #8fc74b;
$teal: #61a198;
$teal-muted: #92c0b4;
$mint: #cde7d6;
$bg-soft: #e3f1e9;

.conecta-page__header {
  position: relative;
  padding: 2rem 1.25rem 1.75rem;
  background: #ffffff;
  border-bottom: 1px solid rgba($green-primary, 0.12);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, $green-primary 0%, $green-light 50%, $teal 100%);
  }
}

.conecta-page__header-bg {
  position: absolute;
  top: -40%;
  right: -10%;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba($green-light, 0.18) 0%, transparent 70%);
  pointer-events: none;
}

.conecta-page__header-content {
  max-width: 960px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.conecta-page__back {
  display: inline-flex;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.4rem 0.85rem;
  border: 1px solid rgba($green-primary, 0.2);
  border-radius: 999px;
  background: rgba($green-primary, 0.06);
  color: $green-primary;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba($green-primary, 0.12);
    transform: translateX(-2px);
  }
}

.conecta-page__brand {
  display: flex;
  align-items: flex-start;
  gap: 1.125rem;
  margin-bottom: 1rem;
}

.conecta-page__brand-text {
  flex: 1;
  min-width: 0;
}

.conecta-page__logo {
  flex-shrink: 0;
  width: 4.25rem;
  height: 4.25rem;
  border-radius: 1rem;
  background: linear-gradient(145deg, #ffffff 0%, $bg-soft 100%);
  border: 1px solid rgba($green-primary, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  box-shadow: 0 8px 24px rgba($green-primary, 0.12);
}

.conecta-page__logo-img {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.conecta-page__eyebrow {
  display: inline-block;
  margin-bottom: 0.35rem;
  padding: 0.2rem 0.65rem;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $green-primary;
  background: rgba($green-primary, 0.08);
  border-radius: 999px;
}

.conecta-page__title {
  margin: 0;
  font-size: 1.85rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.15;
  color: $green-dark;
}

.conecta-page__subtitle {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0.5rem 0 0;
  font-size: 0.95rem;
  font-weight: 500;
  color: $teal;
}

.conecta-page__intro {
  margin: 0;
  padding: 1rem 1.125rem;
  font-size: 0.925rem;
  line-height: 1.6;
  color: #3d5c4f;
  background: linear-gradient(135deg, rgba($bg-soft, 0.9) 0%, rgba($mint, 0.45) 100%);
  border-left: 3px solid $green-primary;
  border-radius: 0 0.75rem 0.75rem 0;
}

.conecta-page__main {
  flex: 1;
  padding: 2rem 1.25rem 2.5rem;
  max-width: 960px;
  margin: 0 auto;
  width: 100%;
}

.conecta-page__main--narrow {
  max-width: 720px;
}

.conecta-page__footer {
  padding: 1.25rem;
  text-align: center;
  background: linear-gradient(to top, rgba($teal-muted, 0.15), transparent);
  border-top: 1px solid rgba($green-primary, 0.08);
}

.conecta-page__footer-text {
  margin: 0 0 0.35rem;
  font-size: 0.8rem;
  color: #4a6b5c;
}

.conecta-page__footer-copy {
  margin: 0;
  font-size: 0.75rem;
  color: #7a9a8a;
}

@media (min-width: 576px) {
  .conecta-page__title {
    font-size: 2.15rem;
  }

  .conecta-page__intro {
    max-width: 36rem;
  }
}

@media (min-width: 992px) {
  .conecta-page__header {
    padding: 2.5rem 2rem 2rem;
  }

  .conecta-page__header-bg {
    width: 420px;
    height: 420px;
    top: -50%;
    right: 5%;
  }

  .conecta-page__brand {
    align-items: center;
  }

  .conecta-page__logo {
    width: 4.75rem;
    height: 4.75rem;
  }

  .conecta-page__title {
    font-size: 2.35rem;
  }

  .conecta-page__main {
    padding: 2.5rem 2rem 3rem;
  }
}

.conecta-form-card {
  background: #ffffff;
  border-radius: 1.25rem;
  border: 1px solid rgba($green-primary, 0.12);
  box-shadow: 0 8px 32px rgba($green-primary, 0.1);
  padding: 1.5rem;

  @media (min-width: 576px) {
    padding: 2rem;
  }
}

.conecta-form-card__title {
  margin: 0 0 0.35rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: $green-dark;
}

.conecta-form-card__subtitle {
  margin: 0 0 1.5rem;
  font-size: 0.9rem;
  color: #4a6b5c;
}

.conecta-form-label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 0.925rem;
  color: $green-dark;

  i {
    color: $green-primary;
    width: 20px;
    text-align: center;
    margin-right: 0.35rem;
  }
}

.conecta-form-control {
  border-radius: 0.65rem;
  border: 1px solid rgba($green-primary, 0.2);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: $green-primary;
    box-shadow: 0 0 0 0.2rem rgba($green-primary, 0.15);
  }
}

.conecta-form-check-label {
  color: #3d5c4f;
  font-size: 0.9rem;

  a {
    color: $green-primary;
    font-weight: 500;
    text-decoration: underline;
  }
}

.conecta-btn-primary {
  background: linear-gradient(135deg, $green-primary 0%, $teal 100%);
  border: none;
  color: #ffffff;
  font-weight: 600;
  border-radius: 0.65rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba($green-primary, 0.3);
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.6;
  }
}

.conecta-btn-secondary {
  background: #ffffff;
  border: 1px solid rgba($green-primary, 0.35);
  color: $green-primary;
  font-weight: 600;
  border-radius: 0.65rem;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba($green-primary, 0.06);
    transform: translateY(-2px);
    color: $green-primary;
  }
}

.conecta-btn-outline {
  background: transparent;
  border: 1px solid rgba($green-primary, 0.3);
  color: $teal;
  font-weight: 500;
  border-radius: 0.65rem;

  &:hover {
    background: rgba($green-primary, 0.06);
    color: $green-dark;
  }
}

.conecta-invalid-feedback {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: #c0392b;
  background-color: rgba(192, 57, 43, 0.08);
  padding: 0.5rem 0.75rem;
  border-radius: 0.35rem;
  border-left: 3px solid #c0392b;
}

.conecta-form-hint {
  font-size: 0.8rem;
  color: #6b8a7a;
  margin-top: 0.35rem;
}

.conecta-badge-success {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.75rem;
  background: rgba($green-primary, 0.1);
  color: $green-dark;
  border-radius: 0.5rem;
  font-size: 0.85rem;
  font-weight: 500;
}
</style>
